import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, Typography, Alert, Space, Tabs, Layout } from 'antd';
import { 
  MailOutlined, 
  LockOutlined, 
  UserOutlined,
  StarOutlined,
  RocketOutlined,
  CheckCircleOutlined,
  GoogleOutlined,
  SendOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../i18n/I18nContext';
import { authApi } from '../api/auth';
import '../components/landing/landing.css';
import { SEOHead } from '../components/SEO/SEOHead';
import { getTelegramStartParam, resolveStartParamRoute } from '../telegram/webApp';
import './Login.css';

const { Title, Paragraph, Text } = Typography;

const Login: React.FC = () => {
  const { loginWithEmail, signUp, loginWithGoogle, loginWithTelegramMiniApp, isAuthenticated, isMiniApp, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [signUpForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState('login');

  // `?next=/app/pro` : retour vers une page interne après connexion (chemins /app/* uniquement)
  const nextParam = searchParams.get('next');
  const nextRoute = nextParam && /^\/app\/[a-z0-9/_-]*$/i.test(nextParam) ? nextParam : null;
  const postLoginRoute = resolveStartParamRoute(getTelegramStartParam()) || nextRoute || '/app/dashboard';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(postLoginRoute);
    }
  }, [isAuthenticated, navigate, postLoginRoute]);

  const handleTelegramMiniAppLogin = async () => {
    setError('');
    setIsSubmitting(true);
    try {
      await loginWithTelegramMiniApp();
      navigate(postLoginRoute);
    } catch (err: any) {
      console.error('Telegram Mini App login error:', err);
      setError(err?.message || t.app.login.telegramAuthError);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Dev uniquement : REACT_APP_RECAPTCHA_DISABLED=true désactive reCAPTCHA (le backend doit avoir RECAPTCHA_DISABLED=true)
  const recaptchaDisabled =
    process.env.NODE_ENV !== 'production' && process.env.REACT_APP_RECAPTCHA_DISABLED === 'true';

  // Load reCAPTCHA script dynamically
  useEffect(() => {
    if (recaptchaDisabled) {
      console.warn('reCAPTCHA disabled (REACT_APP_RECAPTCHA_DISABLED=true, dev only)');
      return;
    }
    const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcNcUgsAAAAAObFb89pZCB2M3tCOjJpALfbM4zM';
    
    // Check if script is already loaded
    if (document.querySelector(`script[src*="recaptcha/enterprise.js"]`)) {
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    
    // Add error handling
    script.onerror = (error) => {
      console.warn('Failed to load reCAPTCHA script:', error);
      // Don't throw - allow app to continue without reCAPTCHA
    };
    
    script.onload = () => {
      console.log('reCAPTCHA script loaded successfully');
    };
    
    try {
      document.head.appendChild(script);
    } catch (error) {
      console.warn('Error appending reCAPTCHA script:', error);
    }
  }, [recaptchaDisabled]);

  const getRecaptchaToken = async (action: string): Promise<string | null> => {
    if (recaptchaDisabled) return null;
    try {
      return new Promise((resolve, reject) => {
        if (typeof window === 'undefined' || !window.grecaptcha) {
          console.warn('reCAPTCHA not loaded');
          resolve(null);
          return;
        }

        window.grecaptcha.enterprise.ready(async () => {
          try {
            const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LcNcUgsAAAAAObFb89pZCB2M3tCOjJpALfbM4zM';
            const token = await window.grecaptcha!.enterprise.execute(
              recaptchaSiteKey,
              { action }
            );
            resolve(token);
          } catch (error: any) {
            console.error('reCAPTCHA execution error:', error);
            reject(error);
          }
        });
      });
    } catch (error: any) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const onLoginFinish = async (values: { email: string; password: string }) => {
    setError('');
    setIsSubmitting(true);

    try {
      // Generate reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('LOGIN');
      
      // Verify reCAPTCHA token with backend
      if (recaptchaToken) {
        try {
          await authApi.verifyRecaptcha(recaptchaToken, 'LOGIN');
        } catch (recaptchaError: any) {
          console.error('reCAPTCHA verification failed:', recaptchaError);
          setError(t.app.login.recaptchaFailed);
          setIsSubmitting(false);
          return;
        }
      }

      // Proceed with Firebase authentication
      await loginWithEmail(values.email, values.password);
      navigate(postLoginRoute);
    } catch (error: any) {
      console.error('Login error:', error);
      let errorMessage = t.app.login.genericError;
      
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        errorMessage = t.app.login.invalidCredentials;
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = t.app.login.wrongPassword;
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = t.app.login.tooManyAttempts;
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = t.app.login.accountDisabled;
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = t.app.login.authNotEnabled;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSignUpFinish = async (values: { email: string; password: string; displayName?: string }) => {
    setError('');
    setIsSubmitting(true);

    try {
      // Generate reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken('SIGNUP');
      
      // Verify reCAPTCHA token with backend
      if (recaptchaToken) {
        try {
          await authApi.verifyRecaptcha(recaptchaToken, 'SIGNUP');
        } catch (recaptchaError: any) {
          console.error('reCAPTCHA verification failed:', recaptchaError);
          setError(t.app.login.recaptchaFailed);
          setIsSubmitting(false);
          return;
        }
      }

      // Proceed with Firebase authentication
      await signUp(values.email, values.password, values.displayName);
      navigate(postLoginRoute);
    } catch (error: any) {
      console.error('Sign up error:', error);
      let errorMessage = t.app.login.genericError;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = t.app.login.emailInUse;
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = t.app.login.emailInvalid;
      } else if (error.code === 'auth/weak-password') {
        errorMessage = t.app.login.passwordMinLength;
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = t.app.login.authNotEnabled;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Redirect if already authenticated
  if (!loading && isAuthenticated) {
    navigate(postLoginRoute, { replace: true });
    return null;
  }

  if (loading && !isSubmitting) {
    return (
      <div className="metamask-login-container">
        <div className="metamask-login-card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <StarOutlined style={{ fontSize: 48, color: '#3c065e', marginBottom: 16 }} spin />
            <Paragraph>{t.app.common.loading}</Paragraph>
          </div>
        </div>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'login',
      label: t.app.login.signIn,
      children: (
        <Form
          form={form}
          name="login"
          onFinish={onLoginFinish}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            label={t.app.login.email}
            rules={[
              { required: true, message: t.app.login.emailRequired },
              { type: 'email', message: t.app.login.emailInvalid },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t.app.login.emailPlaceholder}
              disabled={isSubmitting}
              className="metamask-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={t.app.login.password}
            rules={[{ required: true, message: t.app.login.passwordRequired }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              disabled={isSubmitting}
              className="metamask-input"
            />
          </Form.Item>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ marginBottom: 16 }}
              className="metamask-alert"
            />
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={isSubmitting}
              icon={<RocketOutlined />}
              size="large"
              className="metamask-login-submit-btn"
            >
              {t.app.login.signInButton}
            </Button>
          </Form.Item>

          <div style={{ textAlign: 'center', margin: '16px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
            {t.app.login.or}
          </div>

          <Form.Item>
            <Button
              htmlType="button"
              block
              loading={isSubmitting}
              icon={<GoogleOutlined />}
              size="large"
              className="metamask-google-btn"
              onClick={async () => {
                setError('');
                setIsSubmitting(true);
                try {
                  await loginWithGoogle();
                  navigate(postLoginRoute);
                } catch (error: any) {
                  console.error('Google login error:', error);
                  let errorMessage = t.app.login.googleAuthError;
                  
                  if (error.code === 'auth/popup-closed-by-user') {
                    errorMessage = t.app.login.popupClosed;
                  } else if (error.code === 'auth/popup-blocked') {
                    errorMessage = t.app.login.popupBlocked;
                  } else if (error.code === 'auth/cancelled-popup-request') {
                    errorMessage = t.app.login.popupCancelled;
                  } else if (error.message) {
                    errorMessage = error.message;
                  }
                  
                  setError(errorMessage);
                } finally {
                  setIsSubmitting(false);
                }
              }}
            >
              {t.app.login.signInWithGoogle}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'signup',
      label: t.app.login.signUp,
      children: (
        <Form
          form={signUpForm}
          name="signup"
          onFinish={onSignUpFinish}
          layout="vertical"
          size="large"
          autoComplete="off"
        >
          <Form.Item
            name="displayName"
            label={t.app.login.displayNameOptional}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t.app.login.displayNamePlaceholder}
              disabled={isSubmitting}
              className="metamask-input"
            />
          </Form.Item>

          <Form.Item
            name="email"
            label={t.app.login.email}
            rules={[
              { required: true, message: t.app.login.emailRequired },
              { type: 'email', message: t.app.login.emailInvalid },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t.app.login.emailPlaceholder}
              disabled={isSubmitting}
              className="metamask-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={t.app.login.password}
            rules={[
              { required: true, message: t.app.login.passwordRequiredSignup },
              { min: 6, message: t.app.login.passwordMinLength },
            ]}
            help={t.app.login.minPasswordHint}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="••••••••"
              disabled={isSubmitting}
              className="metamask-input"
            />
          </Form.Item>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError('')}
              style={{ marginBottom: 16 }}
              className="metamask-alert"
            />
          )}

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={isSubmitting}
              icon={<RocketOutlined />}
              size="large"
              className="metamask-login-submit-btn"
            >
              {t.app.login.signUpButton}
            </Button>
          </Form.Item>
        </Form>
      ),
    },
  ];

  return (
    <>
      <SEOHead
        title={t.app.login.seoTitle}
        description={t.app.login.seoDescription}
        canonical="/login"
        noindex={true}
        nofollow={true}
      />
      <Layout className="metamask-login">
        <div className="ts-blob ts-blob--peach metamask-login-blob-1" />
        <div className="ts-blob ts-blob--lilac metamask-login-blob-2" />
        <div className="ts-blob ts-blob--orange metamask-login-blob-3" />
        {/* Login Content */}
        <div className="metamask-login-content">
          <div className="metamask-login-wrapper">
            {/* Logo Section */}
            <div className="metamask-login-logo-section">
              <Link to="/" className="metamask-login-logo-link">
                <img 
                  src="/images/logo.png" 
                  alt="Logo" 
                  className="metamask-login-logo-img"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextElementSibling?.classList.add('show');
                  }}
                />
                <StarOutlined className="metamask-login-logo-fallback" style={{ display: 'none' }} />
                <div className="metamask-login-logo-text">
                  <div className="metamask-login-logo-text-top">Telegram</div>
                  <div className="metamask-login-logo-text-bottom">Stars</div>
                </div>
              </Link>
            </div>

            {/* Login Card */}
            <div className="metamask-login-card">
              <div className="metamask-login-header">
                <Title level={1} className="metamask-login-title">
                  {isSubmitting ? t.app.common.processing : t.app.login.welcome}
                </Title>
                <Paragraph className="metamask-login-description">
                  {t.app.login.subtitle}
                </Paragraph>
              </div>

              {isMiniApp && (
                <div className="metamask-login-telegram">
                  <Button
                    type="primary"
                    block
                    size="large"
                    icon={<SendOutlined />}
                    loading={isSubmitting}
                    onClick={handleTelegramMiniAppLogin}
                    className="metamask-login-submit-btn"
                  >
                    {t.app.login.continueWithTelegram}
                  </Button>
                  {error && (
                    <Alert
                      message={error}
                      type="error"
                      showIcon
                      closable
                      onClose={() => setError('')}
                      style={{ marginTop: 16 }}
                      className="metamask-alert"
                    />
                  )}
                  <div style={{ textAlign: 'center', margin: '16px 0', color: 'rgba(0, 0, 0, 0.45)' }}>
                    {t.app.login.or}
                  </div>
                </div>
              )}

              <Tabs
                items={tabItems}
                activeKey={activeTab}
                centered
                size="large"
                onChange={(key: string) => {
                  setActiveTab(key);
                  setError('');
                }}
                className="metamask-login-tabs"
              />

              <div className="metamask-login-features">
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div className="metamask-login-feature-item" style={{ ['--i' as any]: 0 }}>
                    <CheckCircleOutlined />
                    <Text>{t.app.login.featureInstant}</Text>
                  </div>
                  <div className="metamask-login-feature-item" style={{ ['--i' as any]: 1 }}>
                    <CheckCircleOutlined />
                    <Text>{t.app.login.featureManage}</Text>
                  </div>
                  <div className="metamask-login-feature-item" style={{ ['--i' as any]: 2 }}>
                    <CheckCircleOutlined />
                    <Text>{t.app.login.featureWithdraw}</Text>
                  </div>
                </Space>
              </div>

              <div className="metamask-login-footer-text">
                <Text type="secondary">
                  {t.app.login.tipTelegram}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Login;
